/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-23 (original work) Open Assessment Technologies SA ;
 */

/**
 * Resolves after `time` milliseconds have passed
 * @param {number} time
 * @returns {Promise<unknown>}
 */
export const wait = time =>
    new Promise(resolve => {
        setTimeout(() => {
            resolve();
        }, time);
    });

/**
 * Equivalent to `defaultsDeep` from `lodash` but doesn't merge array values.
 * Mutates `destination` object.
 * Recursively assigns properties of source objects to the destination object for all destination properties that are undefined.
 * Source objects are applied from left to right. Once a property is set, additional values of the same property are ignored.
 * @param {*} destination
 * @param  {...any} sources
 * @returns {Object}
 */
export const defaultsDeepNoArrayMerge = (destination, ...sources) => {
    if (typeof destination === 'undefined' || destination === null) {
        destination = {};
    } else if (typeof destination !== 'object' || Array.isArray(destination)) {
        return destination;
    }
    let result = Object.assign({}, destination);
    for (const source of sources) {
        if (typeof source === 'object' && !Array.isArray(source)) {
            recurseDefaultsDeepNoArrayMerge(result, source);
        }
    }
    Object.assign(destination, result);
    return destination;
};

function recurseDefaultsDeepNoArrayMerge(result, source) {
    for (const key in source) {
        const isSourceUndefined = typeof source[key] === 'undefined';
        const isResultUndefined = typeof result[key] === 'undefined';
        const isSourceAnObject = typeof source[key] === 'object' && !Array.isArray(source[key]) && source[key] !== null;
        const isResultAnObject = typeof result[key] === 'object' && !Array.isArray(result[key]) && result[key] !== null;

        if (isSourceAnObject && (isResultUndefined || isResultAnObject)) {
            if (isResultUndefined) {
                result[key] = {};
            }
            recurseDefaultsDeepNoArrayMerge(result[key], source[key]);
        } else if (isResultUndefined && !isSourceUndefined && !isSourceAnObject) {
            result[key] = source[key];
        }
    }
}
